import { Box, Button, Link, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import { InputField } from "../../../components/Form/InputField";
import * as Yup from "yup";
import { Login } from "../../../service/Auth/authService";
import { notifyError } from "../../../components/ToastContainer/Toast/Toast";
import { useSellerOrgConfig } from "../../../Hooks/useSellerOrgConfig";

const emailSchema = Yup.object().shape({
  email: Yup.string()
    .trim()
    .required("Email is required")
    .test("is-email", "Enter a valid email", (value) => {
      const emailRegex =
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return !!value && emailRegex.test(value);
    }),
  password: Yup.string()
    .required("Password is required")
    .min(6, "Password must be at least 6 characters")
    .max(25, "Password must be 25 characters")
    .matches(/^\S*$/, "Password should not contain spaces")
    .test(
      "uppercase",
      "Password must contain at least ONE uppercase letter (A-Z)",
      (value) => /[A-Z]/.test(value || "")
    )
    .test(
      "number",
      "Password must contain at least ONE number (0-9)",
      (value) => /\d/.test(value || "")
    )
    .test(
      "specialChar",
      "Password must contain at least ONE special character from @$!%*?&#",
      (value) => /[@$!%*?&#]/.test(value || "")
    ),
});

export interface EmailLoginFormProps {
  onSuccess: (response: any) => void | Promise<void>;
  onSwitchToMobile: () => void;
}

const EmailLoginForm: React.FC<EmailLoginFormProps> = ({
  onSuccess,
  onSwitchToMobile,
}) => {
  const { auth } = useSellerOrgConfig();
  const emailPlaceholder = auth.copy.emailPlaceholder ?? "eg : xyz@gmail.com";

  const handleSubmit = async (values: { email: string; password: string }) => {
    const payload = {
      email: values.email.trim(),
      password: values.password,
    };

    try {
      const loginResponse: any = await Login(payload);
      if (loginResponse.status === 201) {
        if (loginResponse.data.success === false) {
          notifyError(loginResponse.data.message);
          return;
        }
        await onSuccess(loginResponse);
      } else {
        notifyError("Login failed. Please check your credentials.");
      }
    } catch (error: any) {
      notifyError(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong"
      );
    }
  };

  return (
    <Formik
      initialValues={{ email: "", password: "" }}
      validationSchema={emailSchema}
      onSubmit={handleSubmit}
    >
      {({ setFieldValue }) => (
        <Form>
          <Box sx={{ mt: { xs: 2, sm: 3 } }}>
            <Box>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "clamp(11px, 1.4vw, 13px)",
                  mb: "8px",
                }}
              >
                Email
              </Typography>
              <InputField
                name="email"
                label=""
                placeholder={emailPlaceholder}
                height="50px"
                onChange={(e) => {
                  setFieldValue("email", e.target.value.trim());
                  if (e.target.value.trim() === "") {
                    setFieldValue("password", "");
                  }
                }}
              />
            </Box>

            <Box sx={{ mt: "10px" }}>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "clamp(11px, 1.4vw, 13px)",
                  mb: "8px",
                }}
              >
                Password
              </Typography>
              <InputField
                name="password"
                label=""
                placeholder="*********"
                height="50px"
                type="password"
                onBlur={(e) =>
                  setFieldValue("password", e.target.value.trim())
                }
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: "10px",
              mb: "10px",
            }}
          >
            <Link
              component="button"
              type="button"
              onClick={onSwitchToMobile}
              sx={{
                "&::before": { display: "none" },
                fontSize: "12px",
                fontWeight: 500,
                lineHeight: "150%",
                cursor: "pointer",
                color: "rgba(10, 159, 226, 1)",
              }}
            >
              Use Mobile Number
            </Link>
            <Link
              href="/forgot-password"
              underline="none"
              variant="caption"
              sx={{
                "&::before": { display: "none" },
                fontWeight: 500,
                fontSize: "clamp(10px, 1.3vw, 11px)",
                lineHeight: "150%",
                color: "rgba(26, 26, 26, 0.5)",
              }}
            >
              Forgot password?
            </Link>
          </Box>

          <Box sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{
                borderRadius: "10px",
                backgroundColor: "rgba(26, 26, 26, 1)",
              }}
            >
              Verify & Continue
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default EmailLoginForm;
